import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Key, Save, Check, Loader2, Shield, ExternalLink, Database } from 'lucide-react';

const SUPPLIER_FORM_KEYS = [
  {
    key: 'jensmobiles',
    label: 'JensMobiles',
    site: 'https://www.jensmobiles.fr',
    hint: 'Pièces détachées — compte pro B2B',
  },
  {
    key: 'mobilax',
    label: 'Mobilax',
    site: 'https://www.mobilax.fr',
    hint: 'Pièces, téléphones et accessoires',
  },
  {
    key: 'lcdphone',
    label: 'LCD-Phone',
    site: 'https://www.lcd-phone.com',
    hint: 'Écrans et pièces LCD',
  },
];

export function SupplierCredentialsPanel({ variant = 'dark' }) {
  const { api } = useAuth();
  const isDark = variant === 'dark';
  const [suppliers, setSuppliers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState('');
  const [supplierEmails, setSupplierEmails] = useState({ jensmobiles: '', mobilax: '', lcdphone: '' });
  const [supplierPasswords, setSupplierPasswords] = useState({ jensmobiles: '', mobilax: '', lcdphone: '' });

  const inputCls = isDark
    ? 'bg-black/30 border-white/10 text-white placeholder:text-gray-500'
    : 'bg-gray-50 border-gray-200 text-gray-900';

  const fetchCredentials = useCallback(async () => {
    try {
      const res = await api.get('/admin/supplier-credentials');
      const sup = res.data.suppliers || {};
      setSuppliers(sup);
      setUpdatedAt(res.data.updated_at);
      setUpdatedBy(res.data.updated_by || '');
      setSupplierEmails({
        jensmobiles: sup.jensmobiles?.email || '',
        mobilax: sup.mobilax?.email || '',
        lcdphone: sup.lcdphone?.email || '',
      });
      setSupplierPasswords({ jensmobiles: '', mobilax: '', lcdphone: '' });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const payload = {};
      SUPPLIER_FORM_KEYS.forEach(({ key }) => {
        const email = supplierEmails[key]?.trim();
        const password = supplierPasswords[key]?.trim();
        if (email || password) {
          payload[key] = {};
          if (email) payload[key].email = email;
          if (password) payload[key].password = password;
        }
      });
      if (!Object.keys(payload).length) {
        alert('Renseignez au moins un email ou mot de passe.');
        return;
      }
      const res = await api.put('/admin/supplier-credentials', { suppliers: payload });
      setSuppliers(res.data.suppliers || {});
      setUpdatedAt(res.data.updated_at);
      setUpdatedBy(res.data.updated_by || '');
      setSupplierPasswords({ jensmobiles: '', mobilax: '', lcdphone: '' });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  const configuredCount = SUPPLIER_FORM_KEYS.filter(({ key }) => suppliers[key]?.configured).length;

  return (
    <div className="space-y-6" data-testid="supplier-credentials-panel">
      <div className={`rounded-xl border p-4 flex gap-3 ${isDark ? 'border-cyan-500/25 bg-cyan-950/20' : 'border-cyan-200 bg-cyan-50'}`}>
        <Shield className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`} />
        <div className={`text-sm leading-relaxed ${isDark ? 'text-cyan-100/90' : 'text-cyan-900'}`}>
          Les mots de passe sont <strong>chiffrés en base</strong> — jamais affichés après enregistrement.
          Laissez le champ mot de passe vide pour conserver le MDP actuel.
          Ensuite, lancez une sync sur{' '}
          <Link to="/admin/cache-fournisseurs" className="text-cyan-300 hover:underline font-semibold">
            Données fournisseurs
          </Link>.
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className={`text-xs uppercase tracking-wide font-semibold ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            {configuredCount}/{SUPPLIER_FORM_KEYS.length} catalogues configurés
          </p>
          {updatedAt && (
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-gray-500'}`}>
              Dernière modification : {new Date(updatedAt).toLocaleString('fr-FR')}
              {updatedBy ? ` · ${updatedBy}` : ''}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="min-h-[44px] flex items-center justify-center gap-2 px-5 rounded-lg text-sm font-bold bg-cyan-600 hover:bg-cyan-500 text-white disabled:opacity-50"
          data-testid="suppliers-save-btn"
        >
          {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saved ? 'Identifiants enregistrés' : saving ? 'Enregistrement…' : 'Enregistrer les identifiants'}
        </button>
      </div>

      <div className="space-y-4">
        {SUPPLIER_FORM_KEYS.map(({ key, label, site, hint }) => {
          const row = suppliers[key] || {};
          const configured = row.configured;
          return (
            <div
              key={key}
              className={`rounded-xl border p-5 space-y-4 ${isDark ? 'border-white/10 bg-white/[0.03]' : 'border-gray-200 bg-white'}`}
              data-testid={`supplier-form-${key}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</h3>
                    {configured ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600/20 text-emerald-300 border border-emerald-500/30">
                        Configuré
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-600/20 text-amber-300 border border-amber-500/30">
                        À compléter
                      </span>
                    )}
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{hint}</p>
                  <a
                    href={site}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:underline mt-1"
                  >
                    {site.replace('https://www.', '')}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <Database className={`w-5 h-5 shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1 uppercase font-semibold">Email compte B2B</label>
                  <input
                    type="email"
                    value={supplierEmails[key] || ''}
                    onChange={(e) => setSupplierEmails((prev) => ({ ...prev, [key]: e.target.value }))}
                    placeholder="votre@email.com"
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm min-h-[44px] ${inputCls}`}
                    autoComplete="off"
                    data-testid={`supplier-email-${key}`}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1 uppercase font-semibold flex items-center gap-1">
                    <Key className="w-3 h-3" /> Mot de passe
                  </label>
                  <input
                    type="password"
                    value={supplierPasswords[key] || ''}
                    onChange={(e) => setSupplierPasswords((prev) => ({ ...prev, [key]: e.target.value }))}
                    placeholder={row.has_password ? 'Laisser vide = garder le MDP actuel' : 'Mot de passe catalogue'}
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm min-h-[44px] ${inputCls}`}
                    autoComplete="new-password"
                    data-testid={`supplier-password-${key}`}
                  />
                  {row.has_password && (
                    <p className="text-xs mt-1 text-gray-500">
                      MDP déjà enregistré
                      {row.uses_env_fallback ? ' · secours .env serveur' : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SupplierCredentialsPanel;
